import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import TagRoundedIcon from "@mui/icons-material/TagRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import PhoneAndroidRoundedIcon from "@mui/icons-material/PhoneAndroidRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import Sidebar from "../Sidebar/Sidebar";
import config from "../../config";
import { useNavigate, useParams } from "react-router-dom";

const drawerWidth = 270;

const sectionCardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(15,23,42,0.06)",
  boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
};

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

// A single labelled field, used throughout the info cards below.
const InfoField = ({ icon: Icon, iconColor, label, value, valueColor, href }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box
      sx={{
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `${iconColor}18`,
        color: iconColor,
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          color: "rgba(15,23,42,0.45)",
        }}
      >
        {label}
      </Typography>
      {href && value && value !== "—" ? (
        <Typography
          component="a"
          href={href}
          sx={{
            fontSize: "0.9rem",
            fontWeight: 700,
            color: valueColor || "#2563EB",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Typography>
      ) : (
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 700,
            color: valueColor || "#0F172A",
            wordBreak: "break-word",
          }}
        >
          {value || "—"}
        </Typography>
      )}
    </Box>
  </Stack>
);

const MandiPartnerDetail = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { id } = useParams();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const abortRef = useRef(null);

  const fetchPartner = useCallback(async () => {
    if (!id) {
      setError("No mandi partner id was provided.");
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError("");

    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${config.apiBaseUrl}/mandi-partner/get/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

      const json = await res.json();
      if (json?.success === false) {
        throw new Error(json?.message || "Failed to load mandi partner.");
      }
      const record = json?.data || json?.partner || (json?._id ? json : null);

      if (!record) {
        throw new Error("Mandi partner not found.");
      }

      setPartner(record);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message || "Failed to load mandi partner.");
      setPartner(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPartner();
    return () => abortRef.current?.abort();
  }, [fetchPartner]);

  const mappedVarieties = Array.isArray(partner?.mappedVarieties) ? partner.mappedVarieties : [];

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          p: { xs: 1.5, sm: 2.5, md: 4 },
          overflowX: "hidden",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1.5} sx={{ mb: { xs: 2, md: 3 } }}>
          {isMobile && (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ border: "1px solid #E0E0E0", borderRadius: "12px" }}>
              <MenuRoundedIcon />
            </IconButton>
          )}

          <IconButton
            onClick={() => navigate(-1)}
            sx={{ border: "1px solid rgba(15,23,42,0.12)", borderRadius: "12px" }}
            aria-label="Go back"
          >
            <ArrowBackRoundedIcon />
          </IconButton>

          <Box
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "1.1rem",
              color: "#fff",
              background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
            }}
          >
            {loading ? <PersonRoundedIcon /> : getInitials(partner?.mandiPartner)}
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {loading ? (
              <>
                <Skeleton variant="text" width={220} height={34} />
                <Skeleton variant="text" width={160} height={20} />
              </>
            ) : (
              <>
                <Typography variant={isMobile ? "h6" : "h4"} fontWeight={800} noWrap>
                  {partner?.mandiPartner || "Mandi Partner"}
                </Typography>
                <Typography sx={{ color: "rgba(15,23,42,0.55)", fontSize: "0.85rem" }}>
                  {[partner?.mandiLocation, partner?.state].filter(Boolean).join(" · ") || "Partner details"}
                </Typography>
              </>
            )}
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Grid item xs={12} md={i === 2 ? 12 : 6} key={i}>
                <Skeleton variant="rounded" height={210} sx={{ borderRadius: "16px" }} />
              </Grid>
            ))}
          </Grid>
        ) : !partner ? (
          !error && (
            <Card elevation={0} sx={{ ...sectionCardSx, p: 5, textAlign: "center" }}>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 40, color: "rgba(15,23,42,0.3)", mb: 1 }} />
              <Typography fontWeight={700}>Mandi partner not found</Typography>
            </Card>
          )
        ) : (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ mb: 3 }}>
              <Chip
                label={partner.isActive ? "Active" : "Inactive"}
                sx={{
                  fontWeight: 700,
                  background: partner.isActive ? "rgba(22,163,74,0.10)" : "rgba(100,116,139,0.12)",
                  color: partner.isActive ? "#166534" : "#475569",
                }}
              />
              <Chip
                icon={<RecordVoiceOverRoundedIcon sx={{ fontSize: "16px !important" }} />}
                label={partner.isIVRCaller ? "IVR Call Enabled" : "IVR Call Disabled"}
                sx={{
                  fontWeight: 700,
                  background: partner.isIVRCaller ? "rgba(124,58,237,0.10)" : "rgba(100,116,139,0.12)",
                  color: partner.isIVRCaller ? "#6D28D9" : "#475569",
                }}
              />
              {partner.mandiCode && (
                <Chip
                  icon={<TagRoundedIcon sx={{ fontSize: "16px !important" }} />}
                  label={`Code: ${partner.mandiCode}`}
                  sx={{ fontWeight: 700, background: "rgba(37,99,235,0.10)", color: "#1D4ED8" }}
                />
              )}
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ ...sectionCardSx, p: { xs: 2, sm: 2.5 }, height: "100%" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <StorefrontRoundedIcon sx={{ color: "#16A34A" }} />
                    <Typography fontWeight={800}>Mandi &amp; Business</Typography>
                  </Stack>

                  <Stack spacing={2}>
                    <InfoField
                      icon={LocationOnRoundedIcon}
                      iconColor="#16A34A"
                      label="Mandi Location"
                      value={partner.mandiLocation}
                    />
                    <Divider />
                    <InfoField icon={MapRoundedIcon} iconColor="#2563EB" label="State" value={partner.state} />
                    <Divider />
                    <InfoField
                      icon={StorefrontRoundedIcon}
                      iconColor="#D97706"
                      label="Business Name"
                      value={partner.businessName}
                    />
                    <Divider />
                    <InfoField icon={TagRoundedIcon} iconColor="#7C3AED" label="Mandi Code" value={partner.mandiCode} />
                  </Stack>
                </Card>
              </Grid>

              {/* Contact info */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ ...sectionCardSx, p: { xs: 2, sm: 2.5 }, height: "100%" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <PhoneRoundedIcon sx={{ color: "#DB2777" }} />
                    <Typography fontWeight={800}>Contact Info</Typography>
                  </Stack>

                  <Stack spacing={2}>
                    <InfoField
                      icon={PhoneRoundedIcon}
                      iconColor="#2563EB"
                      label="Contact Number"
                      value={partner.contactNumber}
                      href={partner.contactNumber ? `tel:${partner.contactNumber}` : undefined}
                    />
                    <Divider />
                    <InfoField
                      icon={PhoneAndroidRoundedIcon}
                      iconColor="#0891B2"
                      label="Alternate Number"
                      value={partner.alternateNumber}
                      href={partner.alternateNumber ? `tel:${partner.alternateNumber}` : undefined}
                    />
                    <Divider />
                    <InfoField
                      icon={EmailRoundedIcon}
                      iconColor="#DB2777"
                      label="Email"
                      value={partner.email}
                      href={partner.email ? `mailto:${partner.email}` : undefined}
                    />
                  </Stack>
                </Card>
              </Grid>

              {/* Mapped varieties */}
              <Grid item xs={12}>
                <Card elevation={0} sx={{ ...sectionCardSx, p: { xs: 2, sm: 2.5 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Inventory2RoundedIcon sx={{ color: "#D97706" }} />
                    <Typography fontWeight={800}>Mapped Varieties</Typography>
                    {mappedVarieties.length > 0 && (
                      <Chip
                        size="small"
                        label={mappedVarieties.length}
                        sx={{ fontWeight: 800, background: "rgba(217,119,6,0.10)", color: "#92400E" }}
                      />
                    )}
                  </Stack>

                  {mappedVarieties.length === 0 ? (
                    <Stack alignItems="center" spacing={1} sx={{ py: 3, color: "rgba(15,23,42,0.4)" }}>
                      <Inventory2RoundedIcon sx={{ fontSize: 34, opacity: 0.3 }} />
                      <Typography sx={{ fontSize: "0.85rem", color: "rgba(15,23,42,0.5)" }}>
                        No varieties mapped yet
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap rowGap={1}>
                      {mappedVarieties.map((variety, index) => {
                        const label =
                          typeof variety === "string"
                            ? variety
                            : variety?.variety || variety?.name || JSON.stringify(variety);
                        return (
                          <Chip
                            key={variety?._id || label || index}
                            label={label}
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.8rem",
                              background: "rgba(217,119,6,0.10)",
                              color: "#92400E",
                              borderRadius: "9px",
                            }}
                          />
                        );
                      })}
                    </Stack>
                  )}
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default MandiPartnerDetail;